import { useState, type FormEvent } from "react";
import { Lightbulb, ListChecks } from "lucide-react";
import { api } from "../../api";
import { Sparkline } from "../Charts";
import { StatusBadge } from "../Ui";
import { useShowMore } from "../Workflow";
import {
  bidRange, competitionTone, filterRows, fmtVolume, keywordsFromText, sortRows, trendValues,
  type KeywordRow, type KeywordStatus, type Place,
} from "../../keywords";
import { SaveBar, SelectAll, SortHeader, toggle, type Sort } from "./shared";

type Mode = "ideas" | "check";
const MIN_VOLUMES = [0, 10, 50, 100, 500, 1000];

export function ResearchTab({ clientId, status, place }: { clientId: number; status: KeywordStatus; place: Place }) {
  const [mode, setMode] = useState<Mode>("ideas");
  const [seeds, setSeeds] = useState("");
  const [url, setUrl] = useState(status.client.website_url || "");
  const [pasted, setPasted] = useState("");
  const [rows, setRows] = useState<KeywordRow[] | null>(null);
  const [numbersFor, setNumbersFor] = useState<Place>(place);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<{ text: string; warn: boolean } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [text, setText] = useState("");
  const [minVolume, setMinVolume] = useState(0);
  const [sort, setSort] = useState<Sort>({ key: "avg_monthly_searches", dir: "desc" });

  // Keyword Planner can't answer without the Google Ads connection: say so before anyone presses the button.
  const ready = Boolean(status.sources[0]?.ok);
  const shown = rows ? sortRows(filterRows(rows, { text, minVolume }), sort.key, sort.dir) : [];
  const { visible, control } = useShowMore(shown, 100, `${text}|${minVolume}|${sort.key}|${sort.dir}|${rows?.length}`);

  async function run(e: FormEvent) {
    e.preventDefault();
    setError(""); setNotice(null);
    const list = keywordsFromText(mode === "ideas" ? seeds : pasted);
    if (mode === "ideas") {
      if (list.length > 20) { setError("Use at most 20 seed keywords."); return; }
      if (!list.length && !url.trim()) { setError("Enter at least one seed keyword or a page URL."); return; }
    } else {
      if (!list.length) { setError("Paste at least one keyword."); return; }
      if (list.length > 1000) { setError("Paste at most 1,000 keywords at a time."); return; }
    }
    setBusy(true);
    try {
      const body = mode === "ideas"
        ? { seeds: list, url: url.trim() || undefined, geo: place.geo, location_id: place.location_id }
        : { keywords: list, geo: place.geo, location_id: place.location_id };
      const r = await api<{ rows: KeywordRow[] }>(`/api/keywords/${clientId}/${mode === "ideas" ? "ideas" : "metrics"}`, { method: "POST", body: JSON.stringify(body) });
      setRows(r.rows);
      setNumbersFor(place);
      setSelected(new Set());
    } catch (err) { setError(err instanceof Error ? err.message : "Keyword lookup failed"); }
    finally { setBusy(false); }
  }

  const keys = shown.map((r) => r.keyword);
  return (
    <>
      <section className="panel kw-research">
        {/* A light switch inside the panel, unlike the page's own tab bar above it. */}
        <div className="kw-mode" role="radiogroup" aria-label="Research mode">
          <button type="button" role="radio" aria-checked={mode === "ideas"} onClick={() => setMode("ideas")}><Lightbulb aria-hidden="true" size={14} />Find ideas</button>
          <button type="button" role="radio" aria-checked={mode === "check"} onClick={() => setMode("check")}><ListChecks aria-hidden="true" size={14} />Check a list</button>
        </div>
        <form className="kw-form" onSubmit={(e) => void run(e)}>
          {mode === "ideas" ? (
            <>
              <label className="kw-field">
                <span className="field-label">Seed keywords</span>
                <textarea rows={3} value={seeds} onChange={(e) => setSeeds(e.target.value)} placeholder={"junk removal\nfurniture removal"} />
                <span className="field-hint">Up to 20, one per line or separated by commas.</span>
              </label>
              <label className="kw-field">
                <span className="field-label">Page URL <small className="muted">(optional)</small></span>
                <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" />
                <span className="field-hint">Google also reads this page for ideas. Clear it to use the seeds only.</span>
              </label>
            </>
          ) : (
            <label className="kw-field kw-field-wide">
              <span className="field-label">Keywords to check</span>
              <textarea rows={5} value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder={"junk removal sacramento\nsame day junk pickup"} />
              <span className="field-hint">Up to 1,000, one per line or separated by commas. Match-type brackets and quotes are ignored.</span>
            </label>
          )}
          <div className="kw-form-actions">
            <button className="button" disabled={busy || !ready}>{busy ? "Asking Google…" : mode === "ideas" ? "Get ideas" : "Get numbers"}</button>
            {/* Not ready: the notice above the panel already says why, so nothing repeats it here. */}
            {ready && <span className="muted">From Google Keyword Planner for {place.geo_label}. Search network only.</span>}
          </div>
          {error && <p role="alert" className="inline-notice danger">{error}</p>}
        </form>
      </section>

      {rows && (
        <section className="panel">
          <div className="panel-heading">
            <div><h2>{mode === "ideas" ? "Keyword ideas" : "Keyword numbers"}</h2><p>{shown.length.toLocaleString()} of {rows.length.toLocaleString()} · {numbersFor.geo_label}</p></div>
            <div className="kw-controls">
              <input type="search" placeholder="Filter keywords" aria-label="Filter keywords" value={text} onChange={(e) => setText(e.target.value)} />
              <select aria-label="Minimum monthly searches" value={minVolume} onChange={(e) => setMinVolume(Number(e.target.value))}>
                {MIN_VOLUMES.map((v) => <option key={v} value={v}>{v ? `${v.toLocaleString()}+ searches` : "Any volume"}</option>)}
              </select>
            </div>
          </div>
          {notice && <p role="status" className={`inline-notice ${notice.warn ? "warning" : "success"}`}>{notice.text}</p>}
          {selected.size > 0 && (
            <SaveBar clientId={clientId} keywords={[...selected]} place={numbersFor} tags={status.tags}
              onSaved={(saved, message, warn) => {
                setRows((current) => current?.map((r) => (saved.includes(r.keyword) ? { ...r, on_list: true } : r)) || null);
                setSelected(new Set());
                setNotice({ text: message, warn });
              }} />
          )}
          {shown.length ? (
            <div className="table-wrap">
              <table className="kw-table">
                <thead><tr>
                  <th className="kw-check"><SelectAll keys={keys} selected={selected} setSelected={setSelected} /></th>
                  <SortHeader label="Keyword" k="keyword" sort={sort} setSort={setSort} />
                  <SortHeader label="Searches / mo" k="avg_monthly_searches" sort={sort} setSort={setSort} numeric title="Average monthly searches over the last 12 months" />
                  <th>12 months</th>
                  <SortHeader label="Competition" k="competition_index" sort={sort} setSort={setSort} numeric title="How many advertisers bid on it, 0-100" />
                  <SortHeader label="Top of page bid" k="high_bid" sort={sort} setSort={setSort} numeric title="Google's low (20th percentile) to high (80th percentile) top-of-page bid" />
                  <th><span className="sr-only">On list</span></th>
                </tr></thead>
                <tbody>
                  {visible.map((r) => (
                    <tr key={r.keyword} className={selected.has(r.keyword) ? "selected" : undefined}>
                      <td className="kw-check"><input type="checkbox" aria-label={`Select ${r.keyword}`} checked={selected.has(r.keyword)} onChange={() => setSelected(toggle(selected, r.keyword))} /></td>
                      <td data-label="Keyword"><strong>{r.keyword}</strong></td>
                      <td className="number-cell" data-label="Searches / mo">{fmtVolume(r.avg_monthly_searches)}</td>
                      <td data-label="12 months"><Sparkline values={trendValues(r.monthly)} label={`Monthly searches for ${r.keyword}: ${trendValues(r.monthly).join(", ")}`} /></td>
                      <td data-label="Competition">
                        {r.competition ? (
                          <>
                            <StatusBadge tone={competitionTone(r.competition)}>{r.competition}</StatusBadge>
                            {r.competition_index != null && <small className="kw-index" title="Google's competition score, 0 to 100">{r.competition_index}</small>}
                          </>
                        ) : <span className="muted">—</span>}
                      </td>
                      <td className="number-cell" data-label="Top of page bid">{bidRange(r.low_bid, r.high_bid, r.currency)}</td>
                      <td data-label="On list">{r.on_list ? <StatusBadge tone="info">On list</StatusBadge> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="muted kw-empty">{rows.length ? "No keywords match these filters." : "Google returned no keywords for this. Try broader seeds or another place."}</p>}
          {control}
        </section>
      )}
    </>
  );
}
